import type { AnyToolPart } from "./types";

export interface ToolImage {
  src: string;
  mediaType: string;
}

function toDataUrl(mediaType: string, data: string): string {
  // MCP already hands us base64; a provider might hand back a full data URL.
  return data.startsWith("data:") ? data : `data:${mediaType};base64,${data}`;
}

const IMAGE_DATA_URL = /^data:(image\/[a-zA-Z0-9.+-]+);base64,/;

/** A value that IS an image — a `data:image/…;base64,…` string, nothing else. */
function asDataUrlImage(value: unknown): ToolImage | null {
  if (typeof value !== "string") return null;
  const mediaType = value.match(IMAGE_DATA_URL)?.[1];
  return mediaType ? { src: value, mediaType } : null;
}

/** One image, or a list of them: a multi-page read arrives as an array. */
function asToolImages(value: unknown): ToolImage[] {
  const one = asToolImage(value);
  if (one) return [one];
  if (!Array.isArray(value) || value.length === 0) return [];
  const images = value.flatMap((item) => asToolImage(item) ?? []);
  return images.length === value.length ? images : [];
}

/** Bytes, not a note about them: a payload the feed's wire cap elided is a
 *  sentence where base64 was, and must read as one rather than draw a broken
 *  image. */
function isBytes(data: string): boolean {
  return data.startsWith("data:")
    ? IMAGE_DATA_URL.test(data)
    : /^[A-Za-z0-9+/=\s]+$/.test(data.slice(0, 64));
}

function asToolImage(block: unknown): ToolImage | null {
  const dataUrl = asDataUrlImage(block);
  if (dataUrl) return dataUrl;
  if (!block || typeof block !== "object") return null;
  const b = block as Record<string, unknown>;
  // A stored picture: `{ url, mediaType }` pointing at the file route, which
  // is what a read returns now that the bytes stay in the store. Durable, so
  // it renders in a reopened thread; never a signed URL (those expire).
  if (
    typeof b.url === "string" &&
    typeof b.mediaType === "string" &&
    b.mediaType.startsWith("image/")
  ) {
    return { src: b.url, mediaType: b.mediaType };
  }
  // MCP image blocks are `{ type: "image", data, mimeType }`; the AI SDK's media
  // block is `{ type: "media", data, mediaType }` and its content-result image
  // part is `{ type: "image-data", data, mediaType }`. Accept any of them.
  const mediaType =
    typeof b.mimeType === "string"
      ? b.mimeType
      : typeof b.mediaType === "string"
        ? b.mediaType
        : undefined;
  const isImage =
    b.type === "image" ||
    b.type === "image-data" ||
    (mediaType?.startsWith("image/") ?? false);
  if (!isImage || typeof b.data !== "string" || !isBytes(b.data)) {
    return null;
  }
  const resolved = mediaType ?? "image/png";
  return { src: toDataUrl(resolved, b.data), mediaType: resolved };
}

/**
 * Tool outputs that carry images (Playwright screenshots, a drawing app's
 * `view`, Bear's `eval` rendering the model) arrive in one of three shapes. Pull
 * the image blocks out so they render as thumbnails instead of a
 * multi-megabyte base64 wall:
 *
 *  - an **MCP-style content-block array** — e.g.
 *    `[{ type: "image", data: "<base64>", mimeType: "image/png" }]`;
 *  - the AI SDK's **`ToolResultOutput` content result** —
 *    `{ type: "content", value: [{ type: "image-data", data, mediaType }, { type: "text", text }] }`
 *    (what an App tool returns to hand the model a viewable image); and
 *  - a **plain result carrying a data URL** — a tool whose declared output is
 *    ordinary JSON and whose value happens to be an image, e.g. Bear's `eval`
 *    returning `{ result: "data:image/png;base64,…", console }` from
 *    `view3dModel()`. A `data:image/…` string IS an image, so it renders as one
 *    wherever it sits; the fields around it stay JSON.
 *
 * `text` collects any `{ type: "text" }` parts so a summary renders as prose, not
 * JSON. `rest` holds anything left over (rendered as JSON), and is `undefined`
 * when nothing remains so that section is dropped.
 */
export function splitToolOutput(output: unknown): {
  images: ToolImage[];
  rest: unknown;
  text?: string;
} {
  // Unwrap a `ToolResultOutput` content result to its parts; everything else
  // (MCP arrays, plain JSON) keeps its existing handling below.
  const blocks =
    output &&
    typeof output === "object" &&
    (output as { type?: unknown }).type === "content" &&
    Array.isArray((output as { value?: unknown }).value)
      ? (output as { value: unknown[] }).value
      : output;

  if (!Array.isArray(blocks)) return splitPlainOutput(blocks);

  const images: ToolImage[] = [];
  const texts: string[] = [];
  const rest: unknown[] = [];
  for (const block of blocks) {
    const image = asToolImage(block);
    if (image) {
      images.push(image);
    } else if (isTextBlock(block)) {
      texts.push(block.text);
    } else {
      rest.push(block);
    }
  }
  return {
    images,
    rest: rest.length > 0 ? rest : undefined,
    text: texts.length > 0 ? texts.join("\n\n") : undefined,
  };
}

/**
 * A tool's own result shape: the whole thing is an image, or one of its fields
 * is one or a list of them (a PDF read's page previews). Anything else passes
 * through untouched as `rest`.
 */
function splitPlainOutput(output: unknown): {
  images: ToolImage[];
  rest: unknown;
} {
  const whole = asDataUrlImage(output);
  if (whole) return { images: [whole], rest: undefined };
  if (!output || typeof output !== "object")
    return { images: [], rest: output };

  const images: ToolImage[] = [];
  const rest: Record<string, unknown> = {};
  let restCount = 0;
  for (const [key, value] of Object.entries(output)) {
    const found = asToolImages(value);
    if (found.length > 0) images.push(...found);
    else {
      rest[key] = value;
      restCount += 1;
    }
  }
  if (images.length === 0) return { images: [], rest: output };
  return { images, rest: restCount > 0 ? rest : undefined };
}

/** A `{ type: "text", text }` content part (AI SDK) — rendered as prose. */
function isTextBlock(block: unknown): block is { type: "text"; text: string } {
  return (
    !!block &&
    typeof block === "object" &&
    (block as { type?: unknown }).type === "text" &&
    typeof (block as { text?: unknown }).text === "string"
  );
}

/**
 * Whether this call came back with something to LOOK at. One rule, two
 * consumers: the row that opens itself to show the image, and the grouping that
 * keeps it out of a collapsed run.
 */
export function hasToolOutputImage(part: AnyToolPart): boolean {
  if (part.state !== "output-available") return false;
  return splitToolOutput(part.output).images.length > 0;
}
